import type { Dispatch, SetStateAction } from 'react'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { ERROR_RATE_LABEL, STOCK_DAYS_LABEL, AuthType } from 'constants/constants'

import { Box, Button, CircularProgress, Grid } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { buildRecDateToNumberIndex, resolveChartClickedData } from 'components/parts/Dashboard/chartClickUtils'
import { initialChartState, type InitialChartState } from 'pages/Dashboard/Dashboard'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { mediumFontSize } from 'style'

import type { MonthReport, Stock } from 'api-client'
import type { MouseHandlerDataParam } from 'recharts'
import type { CurveType } from 'recharts/types/shape/Curve'

/** Recharts 3+: height only accepts number or string of the form `${number}%`. */
function toResponsiveContainerHeight(height: string | number): number | `${number}%` {
  if (typeof height === 'number') return height
  const s = String(height).trim()
  if (/^-?\d+(\.\d+)?%$/.test(s)) return s as `${number}%`
  const n = Number(s)
  if (!Number.isNaN(n)) return n
  return 400
}

// rechartsライブラリのprops
export interface LineCart {
  height: string | number
  xAxis: string
  // 右側の縦軸に表示する項目
  yAxisRight?: string
  yAxisLabel?: string
  yAxisRightLabel?: string
  // y=0 の基準線を表示するか（左軸）
  showZeroLine?: boolean
  // y=0 の基準線の見た目（未指定ならデフォルト）
  zeroLineStroke?: string
  zeroLineStrokeWidth?: number
  line: {
    chartType: string
    name: string
    type?: CurveType
    dataKey: string
    stroke?: string
    // グラフの線の太さを設定できる
    strokeWidth?: number
    fill?: string
    yAxisId?: string
    // 点線かどうか
    dashedLine?: boolean
    // グラフにドットを付与するかどうか
    dot?: boolean
    // グラフを非表示にする項目
    hide?: boolean
  }[]
}

interface ChartProps {
  list: Stock[] | MonthReport[]
  chartDetail: LineCart
  chartState: InitialChartState
  setChartState: Dispatch<SetStateAction<InitialChartState>>
  chartZoom: (
    chartState: InitialChartState,
    setChartState: Dispatch<SetStateAction<InitialChartState>>,
    data: Stock[] | MonthReport[],
    chartDetail: LineCart
  ) => void
  chartZoomOut: (setChartState: Dispatch<SetStateAction<InitialChartState>>) => void
  recDateFormatter: (trRecDateToNumber: number) => string
  isLoading: boolean
  onPlotSelectDate?: (selected: any) => void
  todayLineX?: number | null
  data: Stock[] | MonthReport[]
  /** 左Y軸の目盛りラベルをフォーマットする関数 */
  yAxisTickFormatter?: (value: number) => string
  /** 右Y軸の目盛りラベル（未指定時は左と同じ yAxisTickFormatter を使用） */
  yAxisRightTickFormatter?: (value: number) => string
}

// rechartsライブラリを用いたChartコンポーネント
// 拡大縮小機能については下記を参考
// https://recharts.org/en-US/examples/HighlightAndZoomLineChart
function Chart({
  list,
  chartDetail,
  // 拡大縮小機能に使われるリアクティブなゲッター
  chartState,
  // 拡大縮小機能に使われるリアクティブなセッター
  setChartState,
  chartZoom,
  chartZoomOut,
  recDateFormatter,
  isLoading,
  onPlotSelectDate,
  todayLineX,
  data,
  yAxisTickFormatter,
  yAxisRightTickFormatter,
}: ChartProps) {
  const { loginUserInfo } = useAuthContext()
  const rightYTickFormatter = yAxisRightTickFormatter ?? yAxisTickFormatter
  // NOTE: ドラッグ中(refAreaLeft/refAreaRight)を親stateで更新するとmousemove毎にDashboard全体が再描画され重くなる。
  // そのためドラッグ中の状態はローカルに保持し、mouseup時に1回だけ親stateへ反映する。
  const { left, right, top, bottom, top2, bottom2 } = chartState
  const leftYAxisDomain: [number | string | ((dataMin: number) => number), number | string | ((dataMax: number) => number)] = [
    bottom === 'dataMin' ? (dataMin: number) => Math.floor(dataMin) : bottom,
    top === 'dataMax' ? (dataMax: number) => Math.ceil(dataMax) : top,
  ]
  const rightYAxisDomain: [number | string | ((dataMin: number) => number), number | string | ((dataMax: number) => number)] = [
    bottom2 === 'dataMin' ? (dataMin: number) => Math.floor(dataMin) : bottom2,
    top2 === 'dataMax' ? (dataMax: number) => Math.ceil(dataMax) : top2,
  ]
  const [refAreaLeft, setRefAreaLeft] = useState<string | number | undefined>('')
  const [refAreaRight, setRefAreaRight] = useState<string | number | undefined>('')
  const suppressPlotClickRef = useRef(false)
  const byRecDate = useMemo(() => buildRecDateToNumberIndex(list), [list])

  const plotClickDisabled =
    loginUserInfo?.authType === AuthType.ReadOnly ||
    Boolean(list[0]?.companyId && loginUserInfo?.companyId !== list[0]?.companyId)

  const handlePlotClick = useCallback(
    (state: MouseHandlerDataParam) => {
      if (suppressPlotClickRef.current) {
        suppressPlotClickRef.current = false
        return
      }
      const clickedData = resolveChartClickedData(list, byRecDate, state)
      if (clickedData?.recDate) {
        onPlotSelectDate?.(clickedData)
      }
    },
    [list, byRecDate, onPlotSelectDate]
  )

  return (
    <Box
      sx={{
        width: '100%',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        msUserSelect: 'none',
      }}
      onMouseDownCapture={(e) => {
        // Zoomのドラッグ操作中にブラウザのテキスト選択が走り、周辺の文字がハイライトされるのを防ぐ
        e.preventDefault()
      }}
      onDragStartCapture={(e) => {
        e.preventDefault()
      }}
    >
      {isLoading ? (
        <Grid container alignItems="center" justifyContent="center" direction="column">
          <Grid component="div">
            <CircularProgress color="inherit" size={70} />
          </Grid>
        </Grid>
      ) : (
        <>
          <Button
            type="button"
            variant="contained"
            color="info"
            onClick={() => chartZoomOut(setChartState)}
            // グラフ拡大時のみ有効にする
            disabled={left === initialChartState.left && right === initialChartState.right}
            sx={{ marginBottom: 1 }}
          >
            グラフを元に戻す
          </Button>
          <ResponsiveContainer width="100%" height={toResponsiveContainerHeight(chartDetail.height)}>
            <ComposedChart<Stock | MonthReport>
              data={list}
              onClick={plotClickDisabled ? undefined : handlePlotClick}
              onMouseDown={(e) => {
                // グラフの外側を押下するとeventがnullで渡ってしまうため。nullの場合を無視する
                if (e === null) return

                if (e.activeLabel != null) {
                  suppressPlotClickRef.current = false
                  setRefAreaLeft(e.activeLabel)
                  setRefAreaRight(e.activeLabel)
                }
              }}
              onMouseMove={(e) => {
                if (refAreaLeft && e !== null && e.activeLabel != null) {
                  setRefAreaRight(e.activeLabel)
                  if (e.activeLabel !== refAreaLeft) {
                    suppressPlotClickRef.current = true
                  }
                }
              }}
              // eslint-disable-next-line react/jsx-no-bind
              onMouseUp={() => {
                chartZoom({ ...chartState, refAreaLeft, refAreaRight }, setChartState, data, chartDetail)
                setRefAreaLeft('')
                setRefAreaRight('')
              }}
            >
              <XAxis
                allowDataOverflow
                dataKey={chartDetail.xAxis}
                // グラフ上でマウスで範囲選択時に取得した数値を元に表示範囲を指定する
                domain={[left as string, right as string]}
                type="number"
                tickFormatter={recDateFormatter}
              />
              <YAxis
                yAxisId="1"
                allowDataOverflow
                allowDecimals={false}
                domain={leftYAxisDomain}
                type="number"
                tickFormatter={yAxisTickFormatter ? (value) => yAxisTickFormatter(Number(value)) : undefined}
                label={{
                  value: chartDetail.yAxisLabel ? chartDetail.yAxisLabel : '',
                  angle: 0,
                  dy: 20,
                  dx: -10,
                  position: 'insideTop',
                  fill: 'black',
                }}
              />
              {chartDetail.yAxisRight && (
                <YAxis
                  orientation="right"
                  yAxisId={chartDetail.yAxisRight}
                  allowDataOverflow
                  allowDecimals={Boolean(yAxisRightTickFormatter)}
                  domain={rightYAxisDomain}
                  type="number"
                  tickFormatter={rightYTickFormatter ? (value) => rightYTickFormatter(Number(value)) : undefined}
                  label={{
                    value: chartDetail.yAxisRightLabel ? chartDetail.yAxisRightLabel : '',
                    angle: 0,
                    dy: 20,
                    dx: 10,
                    position: 'insideTop',
                    fill: 'black',
                  }}
                />
              )}
              <Tooltip
                labelFormatter={(value) => recDateFormatter(value)}
                itemStyle={{ color: 'black' }}
                formatter={(value, name) => {
                  if (name === ERROR_RATE_LABEL && typeof value === 'number') {
                    // 誤差率の値をパーセンテージ表記に変換
                    const percentageValue = `${(value * 100).toFixed()}%`
                    return [percentageValue, name]
                  }
                  if (name === STOCK_DAYS_LABEL && typeof value === 'number' && !Number.isInteger(value)) {
                    // 在庫日数を適切な桁数に変換
                    return [value.toFixed(1), name]
                  }
                  // 他の項目はそのまま表示
                  return [value, name]
                }}
              />
              <Legend
                iconSize={30}
                iconType="plainline"
                wrapperStyle={{ fontSize: mediumFontSize }}
                // 凡例のテキストカラーはformatterから修正ができる
                // eslint-disable-next-line arrow-body-style, react/no-unstable-nested-components
                formatter={(value, legendItem: any) => {
                  const isVisible = legendItem?.payload?.style?.visibility !== 'hidden'
                  return (
                    <span style={{ color: 'black' }}>
                      {isVisible ? value : ''}
                    </span>
                  )
                }}
              />
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
              {chartDetail.line.map((value) => {
                if (value.chartType === 'StackedBar') {
                  return (
                    <Bar
                      key={value.name}
                      name={value.name}
                      dataKey={value.dataKey}
                      fill={value.fill}
                      yAxisId={value.yAxisId || '1'}
                      stackId="a"
                      animationDuration={300}
                    />
                  )
                }

                return (
                  <Line
                    key={value.name}
                    name={value.name}
                    type={value.type}
                    dataKey={value.dataKey}
                    fill={value.dataKey === 'shipQuantityInterpolated' ? value.stroke : 'none'}
                    stroke={value.stroke}
                    yAxisId={value.yAxisId || '1'}
                    animationDuration={300}
                    // 線の太さ
                    strokeWidth={value.strokeWidth ?? (value.dataKey === 'shipQuantityInterpolated' ? 0 : 3)}
                    style={{ visibility: value.hide ? 'hidden' : 'visible' }}
                    // dashedLineか通常のlineかどうか
                    strokeDasharray={value.dashedLine ? '3 4' : undefined}
                    // グラフにドットを付与するかどうか
                    dot={value.dot ?? true}
                  />
                )
              })}
              {refAreaLeft && refAreaRight ? (
                <ReferenceArea yAxisId="1" x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} />
              ) : null}
              {chartDetail.showZeroLine ? (
                <ReferenceLine
                  yAxisId="1"
                  y={0}
                  stroke={chartDetail.zeroLineStroke ?? '#7f7f7f'}
                  strokeWidth={chartDetail.zeroLineStrokeWidth ?? 0.3}
                />
              ) : null}
              {todayLineX !== null && todayLineX !== undefined ? (
                <ReferenceLine 
                  yAxisId="1"
                  x={todayLineX} 
                  stroke="#ff0000" 
                  strokeDasharray="3 3" 
                  label={{ value: '本日', position: 'top', fill: '#ff0000' }}
                />
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
    </Box>
  )
}

export default Chart
